/**
 * Application Constants
 * Water Management System
 */

// Environment Variables
export const API_BASE_URL = 'https://maji.binbyte.co.ke/api/v1';
export const APP_NAME = import.meta.env.VITE_APP_NAME || 'Aqua Pure';
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

// Currency
export const CURRENCY = 'KES';
export const CURRENCY_SYMBOL = 'KES';

// Date Formats
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DISPLAY_DATE_FORMAT = 'MMM DD, YYYY';
export const DISPLAY_DATETIME_FORMAT = 'MMM DD, YYYY HH:mm';

// Pagination
export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Status Colors
export const STATUS_COLORS = {
  active: 'success',
  inactive: 'gray',
  pending: 'warning',
  completed: 'success',
  failed: 'error',
  draft: 'gray',
  sent: 'blue',
  paid: 'success',
  overdue: 'error',
  cancelled: 'gray',
  faulty: 'error',
  maintenance: 'warning'
} as const;

// Client Types
export const CLIENT_TYPES = {
  tenant: 'Tenant',
  landlord: 'Landlord'
} as const;

// Meter Status
export const METER_STATUS = {
  active: 'Active',
  inactive: 'Inactive',
  faulty: 'Faulty',
  maintenance: 'Maintenance'
} as const;

// Invoice Status
export const INVOICE_STATUS = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled'
} as const;

// Payment Methods
export const PAYMENT_METHODS = {
  cash: 'Cash',
  bank_transfer: 'Bank Transfer',
  mpesa: 'M-Pesa',
  cheque: 'Cheque',
  card: 'Card'
} as const;

// Payment Status
export const PAYMENT_STATUS = {
  pending: 'Pending',
  completed: 'Completed',
  failed: 'Failed',
  refunded: 'Refunded'
} as const;

// Default Tax Rate
export const DEFAULT_TAX_RATE = 16; // 16% VAT

// Reading Due Days
export const READING_DUE_DAYS = 30; // Readings due after 30 days

// File Upload
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
export const ALLOWED_DOCUMENT_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_DATA: 'user_data',
  THEME: 'theme',
  SIDEBAR_COLLAPSED: 'sidebar_collapsed'
} as const;

// Toast Notification Duration
export const TOAST_DURATION = 3000; // 3 seconds

// API Endpoints
export const ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  
  // Dashboard
  DASHBOARD_STATS: '/dashboard/stats',
  DASHBOARD_ACTIVITIES: '/dashboard/activities',
  DASHBOARD_REVENUE: '/dashboard/revenue',
  DASHBOARD_CONSUMPTION: '/dashboard/consumption',
  
  // Clients
  CLIENTS: '/clients',
  CLIENT_PROPERTIES: (id: string) => `/clients/${id}/properties`,
  CLIENT_ACTIVITIES: (id: string) => `/clients/${id}/activities`,
  CLIENT_FINANCIAL: (id: string) => `/clients/${id}/financial-summary`,
  CLIENT_CONSUMPTION: (id: string) => `/clients/${id}/consumption-summary`,
  
  // Meters
  METERS: '/meters',
  METER_READINGS: (id: string) => `/meters/${id}/readings`,
  METER_STATS: (id: string) => `/meters/${id}/statistics`,
  
  // Readings
  READINGS: '/meter-readings',
  
  // Invoices
  INVOICES: '/invoices',
  INVOICE_PDF: (id: string) => `/invoices/${id}/pdf`,
  INVOICE_SEND: '/invoices/send-email',
  INVOICE_TEMPLATE: '/settings/invoice-template',
  
  // Payments
  PAYMENTS: '/payments',
  PAYMENT_RECEIPT: (id: string) => `/payments/${id}/receipt`,
  PAYMENT_RECEIPT_PDF: (id: string) => `/payments/${id}/receipt/pdf`,
  
  // Reports
  REPORTS_GENERATE: '/reports/generate'
} as const;

// Chart Colors
export const CHART_COLORS = {
  primary: '#1a3d2e',      // forest-green
  secondary: '#7fd957',    // lime-green
  success: '#10b981',      // green
  warning: '#f59e0b',      // orange
  error: '#ef4444',        // red
  info: '#3b82f6',         // blue
  purple: '#a855f7',       // purple
  teal: '#14b8a6'         // teal
} as const;

// Dashboard Period Options
export const DASHBOARD_PERIODS = {
  week: 'Last 7 Days',
  month: 'Last 30 Days',
  quarter: 'Last 3 Months',
  year: 'Last 12 Months'
} as const;